import type { Round } from '@h2h/shared';
import { stagePhaseTone } from './helpers';

export type StageCtaKind = 'picks' | 'compete';

export type StageUserContext = {
  /** Subtle footnote — standalone or under the CTA. */
  note?: string;
  ctaLabel?: string;
  ctaKind?: StageCtaKind;
};

/** Copy + optional CTA for the stage header — one surface, no stacked banners. */
export function stageUserContext({
  round,
  isCurrentStage,
  pickCount,
  picksRequired,
  resolvedCount,
  totalDuels,
  entryEditable = true
}: {
  round?: Round;
  isCurrentStage: boolean;
  pickCount: number;
  picksRequired: number;
  resolvedCount: number;
  totalDuels: number;
  /** False once the stage has rolled out — no re-rank CTA. */
  entryEditable?: boolean;
}): StageUserContext | null {
  const phase = stagePhaseTone(round?.status);
  const hasPartial = pickCount > 0 && pickCount < picksRequired;
  const hasFull = pickCount >= picksRequired;

  if (!hasFull && phase === 'open' && isCurrentStage) {
    if (!entryEditable) {
      return { note: "You didn't finish picking before the stage rolled out." };
    }
    return {
      ctaLabel: hasPartial ? 'Continue picking' : 'Pick duels',
      ctaKind: 'picks',
      note: hasPartial ? `${pickCount} of ${picksRequired} picked` : undefined
    };
  }

  if (!hasFull && phase === 'open') {
    return { note: "You didn't enter this stage." };
  }

  if (!hasFull && (phase === 'live' || phase === 'settling')) {
    return {
      note: phase === 'live' ? 'Stage live — you sat this one out.' : 'Results landing — you sat this one out.'
    };
  }

  if (!hasFull && phase === 'settled') {
    return { note: 'You sat this one out.' };
  }

  if (!hasFull) {
    return { note: 'Picks open closer to the start.' };
  }

  if (phase === 'live' || phase === 'settling') {
    return {
      note:
        resolvedCount > 0
          ? `${resolvedCount} of ${totalDuels} duels decided`
          : 'Points appear as riders finish'
    };
  }

  if (phase === 'open' && isCurrentStage && hasFull) {
    if (entryEditable) {
      return { ctaLabel: 'Re-rank on Compete', ctaKind: 'compete' };
    }
    return { note: 'Ladder locked — stage has rolled out.' };
  }

  if (phase === 'open' && hasFull) {
    return { note: 'Ladder was set before this stage rolled out.' };
  }

  return null;
}
